// SelectField — canonical `select` field primitive (comp-ε GAP-3b-i,
// CHG-2433). Single-choice from a fixed option list. Storage: the chosen
// option `value` (string). Options are supplied via the form field's
// `props.options` (FormDialog forwards `{...field.props}` to the
// primitive).
//
// Contract: specs/_product/field-primitive-registry.md §1 ('select') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the native <select> via FieldShell
// <label htmlFor=id>; validation error in a role="alert" live region +
// aria-invalid + aria-required on the select; aria-describedby links the select
// to its error + helper text nodes; a required select keeps its empty
// placeholder row disabled so it cannot satisfy the field; native <select>
// keyboard focus/list.

import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps extends FieldPrimitiveProps<string> {
  options: SelectOption[];
  /** Placeholder row shown when no value is selected. */
  placeholder?: string;
}

export function SelectField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
  options,
  placeholder = "Select…",
}: SelectFieldProps) {
  const ids = useFieldIds();
  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      <select
        id={ids.inputId}
        name={name}
        value={value ?? ""}
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(ids, error, helperText)}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={fieldControlStyle(Boolean(error))}
      >
        <option value="" disabled={required}>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}
